import {
  createFeatureSelector,
  createSelector,
  MemoizedSelector,
} from '@ngrx/store';
import type { Dictionary } from '@ngrx/entity';
import * as fromVehicles from '../reducers/vehicle.reducer';
import type { FilterItem } from '@fc-vehicles/components/shared/vehicle-filter/vehicles-filter.component';
import type { Vehicle, VehicleGroup, VehicleType } from '@fc-core';
import {
  getAllVehicleGroups,
  getSelectedFilterGroup,
  getSelectedGroup,
} from './vehicle-group.selectors';
import {
  getSearchFields,
  getSearchText,
  getSelectedFilters,
  getVehicleSortBy,
} from './vehicle-filters.selectors';
import type { SearchBy } from '../reducers/vehicle-filters.reducer';
import type { SortBy } from '@fc-shared/ui/sort-by/sort-by';
import { selectRouterParam } from '@fc-store/selectors/router.selectors';
import {
  applySelectedVehicleFilters,
  sortVehicles,
  vehicleSearchFilter,
} from './vehicle-list-filters';

const getAllVehiclesGroup = (vehicles: Vehicle[]) => {
  return {
    id: -1,
    name: `All vehicles (${vehicles?.length})`,
    color: '#B0BEC5',
  };
};

const getCoreVehicleState = createFeatureSelector<fromVehicles.State>(
  fromVehicles.vehiclesFeatureKey,
);

export const getAllVehicleEntities = createSelector(
  getCoreVehicleState,
  fromVehicles.selectEntities,
);

export const getAllRawVehicles = createSelector(
  getCoreVehicleState,
  fromVehicles.selectAll,
);

export const getVehicleTypes = createSelector(
  getCoreVehicleState,
  fromVehicles.getVehicleTypes,
);

const enrichedVehicleCache = new WeakMap<Vehicle, Vehicle>();

export const getAllVehicleFromStore = createSelector(
  getAllRawVehicles,
  getAllVehicleGroups,
  getVehicleTypes,
  (
    vehicles: Vehicle[],
    vehicleGroups: VehicleGroup[],
    vehicleTypes: VehicleType[],
  ) => {
    const typesById = new Map(vehicleTypes?.map((type) => [type.id, type]));
    const groupIdsByVehicleId = groupIdsByVehicle(vehicleGroups);

    // Reuse the previous enriched object when its inputs are unchanged, so a
    // live update to one vehicle doesn't re-create every vehicle downstream.
    return vehicles.map((vehicle) => {
      const typeObj = typesById.get(vehicle.type);
      const groups = groupIdsByVehicleId.get(vehicle.id) ?? [];
      const cached = enrichedVehicleCache.get(vehicle);
      if (
        cached &&
        cached.typeObj === typeObj &&
        haveSameIds(cached.groups, groups)
      ) {
        return cached;
      }
      const enrichedVehicle = { ...vehicle, typeObj, groups };
      enrichedVehicleCache.set(vehicle, enrichedVehicle);
      return enrichedVehicle;
    });
  },
);

export const getSetLocationLoading = createSelector(
  getCoreVehicleState,
  fromVehicles.getSetLocationLoading,
);

export const getUsedVehicleTypes = createSelector(
  getCoreVehicleState,
  fromVehicles.getUsedVehicleTypes,
);

export const getUsedVehicleTypeById = (id: number) =>
  createSelector(getUsedVehicleTypes, (vehicleTypes) =>
    vehicleTypes.find((type) => type.id === id),
  );

const selectAllVehiclesWithoutGroupFilter = createSelector(
  getAllVehicleFromStore,
  getSearchText,
  getVehicleSortBy,
  getSelectedFilters,
  getUsedVehicleTypes,
  getSearchFields,
  (
    vehicles: Vehicle[],
    searchText: string,
    sortBy: SortBy,
    vehicleFilters: FilterItem[],
    vehicleTypes: VehicleType[],
    searchFields: SearchBy[],
  ) => {
    const searchedVehicles = vehicleSearchFilter(
      vehicles,
      searchText,
      vehicleTypes,
      searchFields,
    );
    return applySelectedVehicleFilters(
      sortVehicles(searchedVehicles, sortBy),
      vehicleFilters,
    );
  },
);

export const selectAllVehicles = createSelector(
  selectAllVehiclesWithoutGroupFilter,
  getSelectedFilterGroup,
  (vehicles: Vehicle[], group: SelectedVehicleGroup) => {
    if (!group) return vehicles;
    return filterVehicleBySelectedGroup(vehicles, group);
  },
);

export const getVehiclesForGroupTable = createSelector(
  selectAllVehiclesWithoutGroupFilter,
  getSelectedGroup,
  (vehicles: Vehicle[], group: ReturnType<typeof getSelectedGroup>) => {
    if (!group) return [];
    return filterVehicleBySelectedGroup(vehicles, group);
  },
);

// todo we should get count from backend
// to show all groups with vehicles count
export const getAllVehicleGroupsWithCount = createSelector(
  selectAllVehiclesWithoutGroupFilter,
  getAllVehicleGroups,
  (vehicles: Vehicle[], groups: VehicleGroup[]) => {
    const allVehiclesGroup = getAllVehiclesGroup(vehicles);
    if (!groups || groups?.length === 0) return [allVehiclesGroup];
    const allGroups = groups?.map((group) => ({
      ...group,
      name: `${group.name} (${countVehiclesInGroup(vehicles, group)})`,
    }));
    return [allVehiclesGroup, ...allGroups];
  },
);

export const getSelectedVehicleGroupWithCount = createSelector(
  getSelectedFilterGroup,
  selectAllVehiclesWithoutGroupFilter,
  (group: SelectedVehicleGroup, vehicles: Vehicle[]) => {
    return !group || group.id === -1
      ? getAllVehiclesGroup(vehicles)
      : {
          ...group,
          name: `${group.name} (${countVehiclesInGroup(vehicles, group)})`,
        };
  },
);

const getLoadedFields = createSelector(
  getCoreVehicleState,
  fromVehicles.getLoadedFields,
);
const getLoadedState = createSelector(
  getCoreVehicleState,
  fromVehicles.getLoadedState,
);
export const getLoadingState = createSelector(
  getCoreVehicleState,
  fromVehicles.getLoadingState,
);
const getCount = createSelector(getCoreVehicleState, fromVehicles.getCount);

export const getSelectedVehicleId = createSelector(
  getCoreVehicleState,
  fromVehicles.getSelectedVehicleId,
);
export const getProgress = createSelector(
  getAllRawVehicles,
  getCount,
  getLoadingState,
  (allVehicles: Vehicle[], count: number, loading: boolean) => {
    if (allVehicles?.length === 0 && !loading) return 100;
    if (allVehicles?.length > 0) return (allVehicles?.length / count) * 100;
  },
);

export const getVehicleFromRouterId = createSelector(
  getAllVehicleFromStore,
  selectRouterParam('id'),
  (vehicles, id) => {
    return vehicles.find((vehicle) => vehicle.id === +id);
  },
);

export const getAllVehiclesWithCurrentOptions = createSelector(
  getAllVehicleFromStore,
  getLoadedFields,
  getLoadedState,
  getLoadingState,
  getCount,
  (vehicles, options, loaded, loading, count) => {
    return { vehicles, options, loaded, loading, count };
  },
);

const vehicleByIdSelectors = new Map<
  number,
  MemoizedSelector<object, Vehicle | undefined>
>();

export const getVehicleById = (id: number | string | null | undefined) => {
  const vehicleId = Number(id) || 0;
  let selector = vehicleByIdSelectors.get(vehicleId);
  if (!selector) {
    selector = createSelector(
      getAllVehicleEntities,
      (vehicles) => vehicles[vehicleId],
    );
    vehicleByIdSelectors.set(vehicleId, selector);
  }
  return selector;
};

export const selectVehicleWithGroups = (id: number) => {
  return createSelector(
    getAllVehicleEntities,
    getAllVehicleGroups,
    (vehicles: Dictionary<Vehicle>, vehicleGroups: VehicleGroup[]) => {
      const vehicle = vehicles?.[id];
      if (!vehicle) return undefined;
      return {
        ...vehicle,
        groups: getVehicleGroupIds(vehicleGroups, vehicle.id),
      };
    },
  );
};

const filterVehicleBySelectedGroup = (
  vehicles: Vehicle[],
  group: VehicleGroup,
): Vehicle[] => {
  if (group && group.id !== -1) {
    return vehicles.filter((vehicle) => group.vehicles?.includes(vehicle.id));
  }
  return vehicles;
};

type SelectedVehicleGroup = ReturnType<typeof getSelectedFilterGroup>;

const getVehicleGroupIds = (
  vehicleGroups: VehicleGroup[] | undefined,
  vehicleId: number,
): number[] =>
  (vehicleGroups ?? [])
    .filter((group) => group.vehicles?.includes(vehicleId))
    .map((group) => group.id)
    .filter((groupId): groupId is number => groupId !== undefined);

const groupIdsByVehicle = (
  vehicleGroups: VehicleGroup[] | undefined,
): Map<number, number[]> => {
  const groupIdsByVehicleId = new Map<number, number[]>();
  for (const group of vehicleGroups ?? []) {
    if (group.id === undefined) continue;
    for (const vehicleId of group.vehicles ?? []) {
      const groupIds = groupIdsByVehicleId.get(vehicleId) ?? [];
      groupIds.push(group.id);
      groupIdsByVehicleId.set(vehicleId, groupIds);
    }
  }
  return groupIdsByVehicleId;
};

const haveSameIds = (a: number[] | undefined, b: number[]): boolean =>
  !!a && a.length === b.length && a.every((id, index) => id === b[index]);

const countVehiclesInGroup = (
  vehicles: Vehicle[],
  group: VehicleGroup,
): number => {
  const groupId = group.id;
  if (groupId === undefined) return 0;
  return vehicles.filter((vehicle) => vehicle?.groups?.includes(groupId))
    .length;
};
